import { createApi } from "@reduxjs/toolkit/query/react";
import { retryUnlessClientError } from "../retryConditions";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import { formatUuidT } from "../../flatbuffers/display";

const maxRetries = 2;

const baseQuery = createRetryingBaseQuery(
  process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
  maxRetries,
  retryUnlessClientError,
);

export const channelAPI = createApi({
  reducerPath: "channelAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetChannels"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL CHANNELS */
    getChannels: builder.query({
      query: (body: any) => ({
        url: `/channels/?sort=created_at&sort_direction=desc&page=${body.page}&limit=${body.limit}${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannels"],
    }),
    //* *********************************************************** */
    //* GET ONE CHANNEL */
    getChannel: builder.query({
      query: (body: any) => ({
        url: `/channels/${body.channelId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannels"],
    }),
    //* *********************************************************** */
    //* GET CHANNELS BY SERVER */
    getChannelsByServer: builder.query({
      query: (body: any) => ({
        url: `/channels/?sort=created_at&sort_direction=desc&filter_target=server_id&filter=${
          body.serverId
        }&page=${body.page}&limit=${body.limit}${body.searchAndFilterString || ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetChannels"],
    }),
    //* *********************************************************** */
    //* CREATE CHANNEL */
    postChannel: builder.mutation({
      query: (body: any) => ({
        url: "/channels/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateChannel" },
      invalidatesTags: ["tagGetChannels"],
    }),
    //* *********************************************************** */
    //* EDIT CHANNEL */
    putChannel: builder.mutation({
      query: (body: any) => ({
        url: `/channels/${formatUuidT(body.body.id)}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateChannel" },
      invalidatesTags: ["tagGetChannels"],
    }),
    //* *********************************************************** */
    //* TOGGLE CHANNEL ACTIVE (true partial update — only the active column) */
    patchChannelActive: builder.mutation({
      query: (body: any) => ({
        url: `/channels/${body.id}/active`,
        method: "PATCH",
        body: { active: body.active },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "SetChannelActive" },
      invalidatesTags: ["tagGetChannels"],
    }),
    //* *********************************************************** */
    //* DELETE CHANNEL */
    deleteChannel: builder.mutation({
      query: (body: any) => ({
        url: `/channels/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetChannels"],
    }),
    //* *********************************************************** */
    //* GET MTLS SECRETS (Secrets Manager references for per-channel mTLS) */
    getMtlsSecrets: builder.query<
      { secrets: { name: string; arn: string }[] },
      { prefix?: string; token?: string }
    >({
      query: (arg) => ({
        url: `channels/mtls-secrets${arg?.prefix ? `?prefix=${encodeURIComponent(arg.prefix)}` : ""}`,
        method: "GET",
        headers: { authorization: arg?.token },
      }),
      // MASTER ADMIN only (LIFT-1085): everyone else gets a 403, which no
      // retry will turn into a list.
      extraOptions: { retryCondition: retryUnlessClientError(maxRetries) },
    }),
  }),
});

export const {
  useLazyGetChannelsQuery,
  useLazyGetChannelQuery,
  useLazyGetChannelsByServerQuery,
  usePostChannelMutation,
  usePutChannelMutation,
  usePatchChannelActiveMutation,
  useDeleteChannelMutation,
  useGetMtlsSecretsQuery,
} = channelAPI;
